/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';

import { ID } from '@gh/common-shared-data';
import {
  AbstractRestService,
  arrayOf,
  identity,
  none,
  optimisticLockingOf,
  RestSettings,
  SlicedDataSource,
} from '@gh/core-data';

import { Observable } from 'rxjs';

import { HlpDetailsDto, HlpListItemDto } from './hlp.dto';
import { HlpDeleteMapper, HlpDetailsMapper, HlpListItemMapper, ListingStatus } from './hlp.mapper';
import { QuotationDetailsBase, QuotationVariationDto } from './quotation.dto';

@Injectable()
export class HlpRestService extends AbstractRestService {
  constructor(settings: RestSettings) {
    super(settings);
  }

  findAllAsDataSource(filter: any): SlicedDataSource<HlpListItemDto> {
    return this.createSlicedDataSourceGet(`${this.bp}/hlp`,
                                          filter, identity(), arrayOf(HlpListItemMapper));
  }

  get(id: ID): Observable<HlpDetailsDto> {
    return this.httpGet(`${this.bp}/hlp/${id}`, HlpDetailsMapper);
  }

  getHlpInfo(id: ID): Observable<any> {
    return this.httpGet(`${this.bp}/hlp/${id}/delete/init`, HlpDetailsMapper);
  }

  getClone(id: ID, skipWithdrawnWarningCheck: boolean): Observable<HlpDetailsDto> {
    return this.httpPut(`${this.bp}/hlp/${id}/clone`, { skipWithdrawnWarningCheck }, identity(), HlpDetailsMapper);
  }

  getCloneVariations(id: ID): Observable<QuotationVariationDto[]> {
    return this.httpGet(`${this.bp}/hlp/${id}/clone-quote-changes`, identity());
  }

  update(data: HlpDetailsDto): Observable<HlpDetailsDto> {
    return this.httpPut(`${this.bp}/hlp/${data.houseAndLandPackageId}`, data, HlpDetailsMapper);
  }

  create(data: HlpDetailsDto): Observable<HlpDetailsDto> {
    return this.httpPost(`${this.bp}/hlp`, data, HlpDetailsMapper);
  }

  delete(id: ID, listingStatus?: ListingStatus): Observable<any> {
    const body = {} as any;
    if (listingStatus) {
      body.listingStatus = listingStatus;
    }
    return this.httpPut(`${this.bp}/hlp/${id}/delete`, body, HlpDeleteMapper, optimisticLockingOf(identity()));
  }

  recalculate(data: HlpDetailsDto): Observable<any> {
    return this.httpPut(`${this.bp}/hlp/recalculate`, data, HlpDetailsMapper);
  }

  publish(id: ID): Observable<HlpDetailsDto> {
    return this.httpPost(`${this.bp}/hlp/${id}/publish`, {},  none(), HlpDetailsMapper);
  }

  unPublish(id: ID): Observable<HlpDetailsDto> {
    return this.httpPost(`${this.bp}/hlp/${id}/unpublish`, {},  none(), HlpDetailsMapper);
  }

  createLeadQuote(hlpId: ID, salesFolderId: ID): Observable<QuotationDetailsBase> {
    return this.httpPost(`${this.bp}/hlp/${hlpId}/create-lead-quote?salesFolderId=${salesFolderId}`, {}, none(), identity());
  }
}
